// src/services/readReceipts.js — the read-receipts privacy gate, web side.
//
// ── THE MODEL (mined from the app, A→Z) ──────────────────────────────────────
// Two layers, and the per-chat one wins:
//
//   global   users/{uid}.privacySettings.readReceipts        true | false (default true)
//   per-chat chats/{id}.readReceiptsBy.{uid}                 true | false | ABSENT
//
//   effective = perChat != null ? perChat !== false
//                               : global !== false
//
// Both fields are exactly what ChatInfoScreen.toggleReadReceipts and
// ChatModal.readReceiptsDecision read and write, so a switch flipped on either
// side moves on the other — the chat doc and the user doc are both already
// listened to by every device of mine. It applies to 1:1 AND groups (the app's
// group paths honour the toggle too); the app hides the row for the AI bot and
// Note to Self, so we do the same.
//
// ── FAIL CLOSED ──────────────────────────────────────────────────────────────
// `ready` exists because `undefined !== false` is true: before the two docs
// land, a naive read says "receipts on" and publishes a read the user had
// switched off. That exact race is documented in the app as the "turn receipts
// off, re-enter, ticks go green" bug. Until both sources are loaded we report
// nothing — a privacy default must fail closed. Nothing is lost: once loaded,
// the normal pass reports as usual.
//
// ── WHAT "OFF" ACTUALLY SUPPRESSES ───────────────────────────────────────────
//   • the `seen` flags on the message doc, and `readUpTo[me]` — the two things
//     the SENDER derives a green tick from
// and what it deliberately does NOT suppress:
//   • `delivered` / `deliveredUpTo` — delivery is not a read receipt; grey ✓✓
//     still works, exactly like the phone (report-delivered is never gated)
//   • my own unread badge — `unreadCounts[me] = 0` is my counter, nobody
//     derives a receipt from it

import { doc, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase';

export const OFF_UNKNOWN = { ready: false, enabled: false, perChat: undefined, global: true };

/**
 * Live effective setting for one chat.
 * cb({ ready, enabled, perChat, global }) — `perChat === undefined` means
 * "following my global setting", which the UI says out loud.
 */
export function watchReadReceipts(chatId, uid, cb) {
  if (!chatId || !uid) { cb(OFF_UNKNOWN); return () => {}; }
  let perChat;              // undefined until the chat doc lands
  let global;               // undefined until the user doc lands
  let chatLoaded = false;
  let userLoaded = false;

  const emit = () => {
    const ready = chatLoaded && userLoaded;
    const enabled = ready && (perChat != null ? perChat !== false : global !== false);
    cb({ ready, enabled, perChat, global: global !== false });
  };

  const un1 = onSnapshot(doc(db, 'chats', chatId), (s) => {
    perChat = s.data()?.readReceiptsBy?.[uid];
    chatLoaded = true; emit();
  }, () => { chatLoaded = true; emit(); });

  const un2 = onSnapshot(doc(db, 'users', uid), (s) => {
    global = s.data()?.privacySettings?.readReceipts;
    userLoaded = true; emit();
  }, () => { userLoaded = true; emit(); });

  return () => { un1(); un2(); };
}

/** The app's toggle writes the per-chat override; so does this. */
export function setChatReadReceipts(chatId, uid, value) {
  return updateDoc(doc(db, 'chats', chatId), { [`readReceiptsBy.${uid}`]: !!value });
}

/** Drop the override so this chat follows the global setting again. */
export function clearChatReadReceipts(chatId, uid) {
  return updateDoc(doc(db, 'chats', chatId), { [`readReceiptsBy.${uid}`]: deleteField() });
}

/** The global switch, same field the phone's Privacy screen owns. */
export function setGlobalReadReceipts(uid, value) {
  return updateDoc(doc(db, 'users', uid), { 'privacySettings.readReceipts': !!value });
}
